// Tamirat fiyatlandırması — tek kaynak.
//
// İki mod var (ikisi birlikte kullanılmaz):
//  - 'receipt' (fiş bazlı): fişe tek ücret girilir ya da tüm fiş Ücretsiz / Hesaplanacak seçilir.
//  - 'item' (ürün bazlı): her ürün için ayrı ücret / Ücretsiz / Hesaplanacak + ürün indirimi.
// Her iki modda da fiş toplamına yüzde veya tutar indirimi yapılabilir; ürün bazlı modda bu sepet
// indirimi ücretli ürünlere tutarları oranında paylaştırılır (son ürün kuruş farkını alır).
//
// repairs.estimated_price HER ZAMAN indirim sonrası net tutardır — ödeme, kalan tutar, kasa ve
// raporlar eskisi gibi bu alandan çalışır. Ara toplam / indirim ayrıntısı repairs.pricing (JSON)
// ve items[].price* alanlarında durur; sadece gösterim (form, detay, fiş) içindir.

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export const parseRepairPricing = (repair) => {
  let p = repair?.pricing;
  if (typeof p === 'string') {
    try { p = JSON.parse(p); } catch (e) { p = null; }
  }
  return p && typeof p === 'object' ? p : null;
};

export const parseRepairItems = (repair) => {
  let items = repair?.items;
  if (typeof items === 'string') {
    try { items = JSON.parse(items); } catch (e) { items = []; }
  }
  return Array.isArray(items) ? items : [];
};

const calcDiscount = (base, type, value) => {
  const v = Number(value) || 0;
  if (!(base > 0) || !(v > 0)) return 0;
  if (type === 'percent') return round2(base * Math.min(v, 100) / 100);
  return round2(Math.min(v, base));
};

// items: formdaki ürünler (price_type, price, discount_type, discount_value)
// Döner: { price_type, subtotal, item_discount_total, gross_total, discount_amount, total, items }
//  - subtotal: ürünlerin indirimsiz toplamı (fiş bazlıda girilen ücret)
//  - gross_total: ürün indirimlerinden sonra, sepet indiriminden önce
//  - total: net (estimated_price)
export const computeRepairPricing = ({ mode, items = [], receiptPriceType = 'normal', receiptPrice = 0, discountType = 'amount', discountValue = 0 }) => {
  if (mode !== 'item') {
    const subtotal = receiptPriceType === 'normal' ? round2(receiptPrice) : 0;
    const discount = receiptPriceType === 'normal' ? calcDiscount(subtotal, discountType, discountValue) : 0;
    const total = round2(subtotal - discount);
    return {
      // %100 indirim → fiş ücretsiz sayılır
      price_type: receiptPriceType === 'normal' && subtotal > 0 && !(total > 0) ? 'free' : receiptPriceType,
      subtotal,
      item_discount_total: 0,
      gross_total: subtotal,
      discount_amount: discount,
      total,
      items: items.map(stripItemPricing)
    };
  }

  const computed = items.map(item => {
    const type = item.price_type || 'normal';
    const price = type === 'normal' ? round2(item.price) : 0;
    const itemDiscount = type === 'normal' ? calcDiscount(price, item.discount_type, item.discount_value) : 0;
    return { ...item, price_type: type, _net: round2(price - itemDiscount), _price: price, _itemDiscount: itemDiscount };
  });

  const subtotal = round2(computed.reduce((s, i) => s + i._price, 0));
  const itemDiscountTotal = round2(computed.reduce((s, i) => s + i._itemDiscount, 0));
  const grossTotal = round2(subtotal - itemDiscountTotal);
  const discount = calcDiscount(grossTotal, discountType, discountValue);

  // Sepet indirimini ücretli ürünlere oranla dağıt
  const payable = computed.filter(i => i._net > 0);
  let distributed = 0;
  const shares = new Map();
  payable.forEach((item, idx) => {
    const share = idx === payable.length - 1
      ? round2(discount - distributed)
      : round2(discount * item._net / grossTotal);
    distributed = round2(distributed + share);
    shares.set(item, share);
  });

  const outItems = computed.map(item => {
    const { _net, _price, _itemDiscount, ...rest } = item;
    const basketShare = shares.get(item) || 0;
    return {
      ...rest,
      price: item.price_type === 'normal' ? _price : null,
      discount_type: item.price_type === 'normal' && _itemDiscount > 0 ? (item.discount_type || 'amount') : null,
      discount_value: item.price_type === 'normal' && _itemDiscount > 0 ? Number(item.discount_value) || 0 : null,
      discount_amount: _itemDiscount,
      basket_discount: basketShare,
      final_price: item.price_type === 'normal' ? round2(_net - basketShare) : 0
    };
  });

  const hasPending = computed.some(i => i.price_type === 'pending');
  const total = round2(grossTotal - discount);
  let priceType = 'normal';
  if (hasPending) priceType = 'pending';
  else if (!(total > 0)) priceType = 'free';

  return {
    price_type: priceType,
    subtotal,
    item_discount_total: itemDiscountTotal,
    gross_total: grossTotal,
    discount_amount: discount,
    total,
    items: outItems
  };
};

export const stripItemPricing = (item) => {
  const { price_type, price, discount_type, discount_value, discount_amount, basket_discount, final_price, ...rest } = item;
  return rest;
};

// Kayıttan forma: ürün bazlı fiyat alanlarını input (string) değerlerine çevir
export const itemPricingToForm = (item) => ({
  ...item,
  price_type: item.price_type || 'normal',
  price: item.price != null && Number(item.price) > 0 ? String(item.price) : '',
  discount_type: item.discount_type || 'amount',
  discount_value: item.discount_value != null && Number(item.discount_value) > 0 ? String(item.discount_value) : ''
});

const fmt = (n) => `₺${Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
export const formatTL = fmt;

export const discountLabel = (type, value) => (type === 'percent' ? `%${Number(value).toLocaleString('tr-TR')}` : fmt(value));

// Ürün satırı için kısa fiyat etiketi (liste/fiş): "Ücretsiz" / "Hesaplanacak" / "₺150"
export const itemPriceLabel = (item) => {
  if (!item || item.price_type == null) return null;
  if (item.price_type === 'free') return 'Ücretsiz';
  if (item.price_type === 'pending') return 'Hesaplanacak';
  return fmt(item.final_price != null ? item.final_price : item.price);
};
